import styled from '@emotion/styled';
import {
    CheckboxItem,
    Content,
    Group,
    Item,
    ItemIndicator,
    Label,
    Portal,
    RadioGroup,
    RadioItem,
    Root,
    Separator,
    Sub,
    SubContent,
    SubTrigger,
    Trigger,
} from '@radix-ui/react-dropdown-menu';
import is from '@sindresorhus/is';

import { Tooltip } from '../tooltip/Tooltip';

import type { CSSObject } from '@emotion/styled';
import type {
    DropdownMenuCheckboxItemProps,
    DropdownMenuContentProps,
    DropdownMenuItemProps,
    DropdownMenuProps,
    DropdownMenuRadioGroupProps,
    DropdownMenuRadioItemProps,
    DropdownMenuSubContentProps,
    DropdownMenuSubProps,
    DropdownMenuSubTriggerProps,
    DropdownMenuTriggerProps,
} from '@radix-ui/react-dropdown-menu';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';

export type DropdownMenuSeparatorProps = {};

export type DropdownMenuLabelProps = {
    readonly children: ReactNode;
};

export type DropdownMenuGroupProps = {
    readonly children: ReactNode;
};

const getContentStyles = (theme: any): CSSObject => ({
    backgroundColor: theme.colors.gray[100],
    border: `1px solid ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.xl,
    boxShadow: theme.shadows.medium,
    fontFamily: theme.typography.body.M.semibold.fontFamily,
    minWidth: '220px',
    overflow: 'hidden',
    padding: theme.spacing.xs,
    zIndex: 1000,
});

const getDisabledStyles = (theme: any): CSSObject => ({
    '&[data-disabled]': {
        color: theme.colors.gray[400],
        cursor: 'not-allowed',
        pointerEvents: 'none',
    },
});

const getBaseItemStyles = (theme: any): Record<string, any> => ({
    '&:hover': {
        backgroundColor: theme.colors.gray[200],
    },
    alignItems: 'center',
    borderRadius: theme.borderRadius.s,
    color: theme.colors.gray[900],
    cursor: 'pointer',
    display: 'flex',
    fontSize: theme.typography.body.XS.regular.fontSize,
    fontWeight: theme.typography.body.XS.regular.fontWeight,
    lineHeight: theme.typography.body.XS.regular.lineHeight,
    padding: `${theme.spacing.s} ${theme.spacing.m}`,
    position: 'relative',
    userSelect: 'none',
    ...getDisabledStyles(theme),
});

const getHighlightedStyles = (theme: any): CSSObject => ({
    '&[data-highlighted]': {
        backgroundColor: theme.colors.gray[200],
        color: theme.colors.gray[900],
        outline: 'none',
    },
});

const getCheckedStyles = (theme: any): CSSObject => ({
    '&[data-state="checked"]': {
        backgroundColor: theme.colors.gray[200],
    },
});

const StyledContent = styled(Content)(({ theme }) => getContentStyles(theme));

const StyledItem = styled(Item)<DropdownMenuItemProps>(({ theme }) => ({
    ...getBaseItemStyles(theme),
    '& svg': {
        color: theme.colors.gray[700],
    },
    '&[data-highlighted]': {
        backgroundColor: theme.colors.gray[200],
        color: theme.colors.gray[900],
        outline: 'none',
    },
    alignItems: 'center',
    gap: theme.spacing.xs,
}));

const StyledItemIndicator = styled(ItemIndicator)(({ theme }) => ({
    alignItems: 'center',
    color: theme.colors.gray[900],
    display: 'inline-flex',
    justifyContent: 'center',
    marginLeft: theme.spacing.s,
}));

const StyledCheckboxItem = styled(CheckboxItem)<DropdownMenuCheckboxItemProps>(({ theme }) => ({
    ...getBaseItemStyles(theme),
    ...getHighlightedStyles(theme),
    ...getCheckedStyles(theme),
}));

const StyledSeparator = styled(Separator)(({ theme }) => ({
    backgroundColor: theme.colors.gray[300],
    height: '1px',
    margin: theme.spacing.xs,
}));

const StyledGroup = styled(Group)(({ theme }) => getDisabledStyles(theme));

const StyledLabel = styled(Label)(({ theme }) => ({
    color: theme.colors.gray[700],
    fontSize: theme.typography.body.XS.regular.fontSize,
    fontWeight: theme.typography.body.XS.regular.fontWeight,
    padding: theme.spacing.s,
}));

const StyledSubTrigger = styled(SubTrigger)<DropdownMenuSubTriggerProps>(({ theme }) => ({
    ...getBaseItemStyles(theme),
    '&[data-state="open"]': {
        backgroundColor: theme.colors.gray[100],
    },
    ...getHighlightedStyles(theme),
    justifyContent: 'space-between',
}));

const StyledSubContent = styled(SubContent)<DropdownMenuSubContentProps>(({ theme }) => getContentStyles(theme));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => getDisabledStyles(theme));

const StyledRadioItem = styled(RadioItem)<DropdownMenuRadioItemProps>(({ theme }) => ({
    ...getBaseItemStyles(theme),
    ...getHighlightedStyles(theme),
    ...getCheckedStyles(theme),
}));

export const DropdownMenu: FunctionComponent<DropdownMenuProps> = ({ children, onOpenChange, open, ...props }) => {
    return (
        <Root open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Root>
    );
};

export const DropdownMenuTrigger: FunctionComponent<PropsWithChildren<DropdownMenuTriggerProps>> = ({
    children,
    ...props
}) => {
    return (
        <Trigger asChild={true} {...props}>
            {children}
        </Trigger>
    );
};

export const DropdownMenuItem: FunctionComponent<DropdownMenuItemProps & { readonly tooltipContent?: string }> = ({
    children,
    disabled,
    onSelect,
    tooltipContent,
    ...props
}) => {
    const menuItem = (
        <StyledItem disabled={disabled} onSelect={onSelect} {...props}>
            {children}
        </StyledItem>
    );

    if (disabled === true && is.nonEmptyString(tooltipContent)) {
        return (
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <div style={{ cursor: 'not-allowed', display: 'flex' }}>{menuItem}</div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="right" sideOffset={5}>
                        {tooltipContent}
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        );
    }

    return menuItem;
};

export const DropdownMenuCheckboxItem: FunctionComponent<DropdownMenuCheckboxItemProps> = ({
    checked,
    children,
    disabled,
    onCheckedChange,
    ...props
}) => {
    return (
        <StyledCheckboxItem checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} {...props}>
            {children}
        </StyledCheckboxItem>
    );
};

export const DropdownMenuSeparator: FunctionComponent<DropdownMenuSeparatorProps> = ({ ...props }) => {
    return <StyledSeparator {...props} />;
};

export const DropdownMenuLabel: FunctionComponent<DropdownMenuLabelProps> = ({ children, ...props }) => {
    return <StyledLabel {...props}>{children}</StyledLabel>;
};

export const DropdownMenuGroup: FunctionComponent<DropdownMenuGroupProps> = ({ children, ...props }) => {
    return <StyledGroup {...props}>{children}</StyledGroup>;
};

export const DropdownMenuSub: FunctionComponent<DropdownMenuSubProps> = ({
    children,
    onOpenChange,
    open,
    ...props
}) => {
    return (
        <Sub open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Sub>
    );
};

export const DropdownMenuRadioGroup: FunctionComponent<DropdownMenuRadioGroupProps> = ({ children, ...props }) => {
    return <StyledRadioGroup {...props}>{children}</StyledRadioGroup>;
};

export const DropdownMenuRadioItem: FunctionComponent<DropdownMenuRadioItemProps> = ({ children, ...props }) => {
    return <StyledRadioItem {...props}>{children}</StyledRadioItem>;
};

export const DropdownMenuSubTrigger: FunctionComponent<DropdownMenuSubTriggerProps> = ({ children, ...props }) => {
    return <StyledSubTrigger {...props}>{children}</StyledSubTrigger>;
};

export const DropdownMenuSubContent: FunctionComponent<DropdownMenuSubContentProps> = ({
    alignOffset,
    children,
    sideOffset,
    ...props
}) => {
    return (
        <Portal>
            <StyledSubContent alignOffset={alignOffset} sideOffset={sideOffset} {...props}>
                {children}
            </StyledSubContent>
        </Portal>
    );
};

export const DropdownMenuContent: FunctionComponent<DropdownMenuContentProps> = ({ children, ...props }) => {
    return (
        <Portal>
            <StyledContent {...props}>{children}</StyledContent>
        </Portal>
    );
};

export const DropdownMenuItemIndicator: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return <StyledItemIndicator>{children}</StyledItemIndicator>;
};
